/**
 * Where a blank line of the Process screen gets its starting value.
 *
 * The Process screen shows every line of a document's output format and lets
 * the association answer it. A line the order already answered keeps that
 * answer. A line still blank is seeded, in this order:
 *
 *   1. the association's Project Details — the questionnaire answered once
 *      for the whole association ("in an association of 900 units, making
 *      them fill this in again and again is a problem");
 *   2. a field the spec says to copy from (`defaultFrom`): the preparer's
 *      company is the management company the order already carries;
 *   3. the person doing the processing, for the preparer block, and today's
 *      date for "date completed".
 *
 * Seeding never overwrites; it only fills what is empty, and what it fills is
 * still editable before the document is released.
 */

import { allFields } from "@/forms/fannie-mae-1076/spec.mjs";
import { allFields as mamFields } from "@/forms/mid-america-condo-2-4/spec.mjs";
import { allFields as pcbFields } from "@/forms/pcb-full-review/spec.mjs";

const DEFAULT_FROM = new Map<string, string>(
  [...allFields(), ...(mamFields() as any[]), ...(pcbFields() as any[])]
    .filter((f) => f.defaultFrom)
    .map((f) => [f.name, f.defaultFrom as string]),
);

/** Where a seeded value came from — shown as a per-line badge on Process. */
export type SeedSource = "project_details" | "order" | "operator" | "";

export interface SeedContext {
  /** Spec field name → the association's standing answer. */
  projectDetails?: Record<string, string> | null;
  /** Form field name → value stored on the order (the `system` fields). */
  orderAnswers?: Record<string, string> | null;
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone_number?: string | null;
    job_title?: string | null;
  } | null;
}

function present(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The value a blank line should start with — and where it came from. */
export function seedValueWithSource(
  name: string | null | undefined,
  ctx: SeedContext,
): { value: string; source: SeedSource } {
  if (!name) return { value: "", source: "" };

  const fromAssociation = ctx.projectDetails?.[name];
  if (present(fromAssociation)) return { value: fromAssociation, source: "project_details" };

  const copyFrom = DEFAULT_FROM.get(name);
  if (copyFrom) {
    const copied = ctx.orderAnswers?.[copyFrom];
    if (present(copied)) return { value: copied, source: "order" };
  }

  const user = ctx.user;
  switch (name) {
    case "prep_name": {
      const value = [user?.firstName, user?.lastName].filter(present).join(" ");
      return { value, source: value ? "operator" : "" };
    }
    case "prep_email":
      return present(user?.email) ? { value: user!.email!, source: "operator" } : { value: "", source: "" };
    case "prep_phone":
      return present(user?.phone_number) ? { value: user!.phone_number!, source: "operator" } : { value: "", source: "" };
    case "prep_title":
      return present(user?.job_title) ? { value: user!.job_title!, source: "operator" } : { value: "", source: "" };
    case "prep_date":
      return { value: todayIso(), source: "operator" };
    default:
      return { value: "", source: "" };
  }
}

/** The value a blank line named `name` should start with, or "" for nothing. */
export function seedValue(name: string | null | undefined, ctx: SeedContext): string {
  return seedValueWithSource(name, ctx).value;
}

/** "Yes", "true", "on", "1" — the ways a checked box can have been stored. */
export function isYesValue(value: unknown): boolean {
  return /^(yes|y|true|on|1)$/i.test(String(value ?? "").trim());
}

/** The options of a select field, as the form builder stores them in `content`. */
export function parseSelectOptions(content: string | null | undefined): Array<{ label: string; value: string }> {
  if (!content) return [];
  try {
    const parsed = JSON.parse(content);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((o: any) =>
        typeof o === "string"
          ? { label: o, value: o }
          : { label: String(o?.label ?? o?.value ?? ""), value: String(o?.value ?? o?.label ?? "") },
      )
      .filter((o) => o.value !== "");
  } catch {
    return [];
  }
}
